import { cx } from '@flexbyt/widget-preview';
import type { ReactNode } from 'react';
import {
    type Inline,
    type LegalBlock,
    type LegalDoc,
    type ListItem,
} from '../../content/legal.js';
import { breadcrumbSchema, jsonLd, seo } from '../../lib/seo.js';
import { TextLink, linkClass } from '../ui/button.js';
import { Measure, Section } from '../ui/layout.js';
import { Heading, Text } from '../ui/type.js';
import { Breadcrumbs } from './Breadcrumbs.js';

/**
 * A legal document, set as type.
 *
 * ===========================================================================
 * One chrome, three documents
 * ===========================================================================
 * Privacy, terms and cookies are the same kind of page: a title, a date, a
 * long document. The words differ; the frame must not. Building that frame
 * three times is how one of them eventually ships without breadcrumbs or
 * with a different measure from the other two.
 *
 * The body is the live text from reviewsjet.com, not a paraphrase. Presentation
 * is this site's type scale; the clauses are not rewritten to fit it.
 */

export function legalHead(doc: LegalDoc) {
  const { meta, links } = seo({
    title: doc.title,
    description: doc.description,
    path: doc.path,
  });

  return {
    meta,
    links,
    scripts: [
      {
        type: 'application/ld+json',
        children: jsonLd(
          breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: doc.title, path: doc.path },
          ])
        ),
      },
    ],
  };
}

export function LegalDocument({ doc }: { doc: LegalDoc }) {
  return (
    <Section size="tight">
      <Breadcrumbs
        trail={[
          { name: 'Home', path: '/' },
          { name: doc.title, path: doc.path },
        ]}
      />

      <Measure size="prose" className="mt-8">
        <Heading level={1} size="display">
          {doc.title}
        </Heading>
        <Text size="small" tone="subtle" className="mt-3">
          Last updated {doc.updated}
        </Text>

        {doc.showToc ? <TableOfContents sections={doc.sections} /> : null}

        {doc.sections.map((section) => (
          <section key={section.id} id={section.id} className="scroll-mt-24">
            <Heading level={2} size="title" className="mt-12">
              {section.title}
            </Heading>
            <div className="mt-4 space-y-4">
              {section.blocks.map((block, index) => (
                <Block key={index} block={block} />
              ))}
            </div>
          </section>
        ))}
      </Measure>
    </Section>
  );
}

function TableOfContents({
  sections,
}: {
  sections: LegalDoc['sections'];
}) {
  return (
    <nav aria-label="Table of contents" className="mt-10">
      <p className="text-body-sm font-semibold text-ink-950">Table of contents</p>
      <ol className="mt-3 space-y-1.5">
        {sections.map((section) => (
          <li key={section.id}>
            <a href={`#${section.id}`} className={cx('text-body-sm', linkClass)}>
              {section.title}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
}

function Block({ block }: { block: LegalBlock }) {
  switch (block.type) {
    case 'p':
      return <p className="text-body text-ink-600">{rich(block.parts)}</p>;
    case 'h3':
      return (
        <Heading level={3} size="label" className="pt-2">
          {block.text}
        </Heading>
      );
    case 'list':
      return <List items={block.items} />;
    case 'address':
      return (
        <address className="not-italic text-body text-ink-600">
          {block.lines.map((line, index) => (
            <div key={index}>{typeof line === 'string' ? line : rich(line)}</div>
          ))}
        </address>
      );
  }
}

function List({ items }: { items: ListItem[] }) {
  return (
    <ul className="list-disc space-y-2 pl-5 text-body text-ink-600">
      {items.map((item, index) => (
        <ListEntry key={index} item={item} />
      ))}
    </ul>
  );
}

function ListEntry({ item }: { item: ListItem }) {
  if (typeof item === 'string') {
    return <li>{item}</li>;
  }

  if (Array.isArray(item)) {
    return <li>{rich(item)}</li>;
  }

  return (
    <li>
      {rich(item.parts)}
      {item.children?.length ? (
        <ul className="mt-2 list-disc space-y-2 pl-5">
          {item.children.map((child, index) => (
            <ListEntry key={index} item={child} />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

function rich(parts: Inline[]): ReactNode {
  return parts.map((part, index) => {
    if (typeof part === 'string') return <span key={index}>{part}</span>;
    if ('bold' in part) {
      return (
        <strong key={index} className="font-semibold text-ink-900">
          {part.text}
        </strong>
      );
    }

    if (part.href.startsWith('/')) {
      return (
        <TextLink key={index} to={part.href}>
          {part.label}
        </TextLink>
      );
    }

    return (
      <a
        key={index}
        href={part.href}
        className={linkClass}
        {...(part.external
          ? { target: '_blank', rel: 'noopener noreferrer' }
          : {})}
      >
        {part.label}
      </a>
    );
  });
}
